import React from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Card, 
  CardContent, 
  Divider, 
  Button, 
  Chip, 
  useTheme,
  Stack,
  Alert
} from '@mui/material';
import { 
  TrendingUp, 
  TrendingDown, 
  Warning, 
  QuestionAnswer, 
  ShowChart, 
  ArrowForward, 
  CheckCircle, 
  ErrorOutline,
  HelpOutline,
  AttachMoney,
  Timeline,
  ArrowUpward,
  ArrowDownward,
  CompareArrows,
  BarChart,
  MonetizationOn,
  Assessment,
  Info,
  Help
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import { InvestmentRecommendation, MarketReportFull } from '@/types/investment';
import { getSignalColor } from '../../theme/themesV3';

// Styled components
const QuestionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'visible',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  }
}));

const QuestionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.custom.bannerBg,
}));

const AnswerContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

// Types
interface MarketQuestionsViewProps {
  marketReport: MarketReportFull;
  onTickerSelect?: (ticker: string) => void;
  renderMarketRating: (rating: string) => React.ReactElement;
  renderMarketTrend: (trend: string) => React.ReactElement;
  onTabChange: (index: number, subtab?: number) => void;
}

const MarketQuestionsView: React.FC<MarketQuestionsViewProps> = ({ 
  marketReport, 
  onTickerSelect,
  renderMarketRating,
  renderMarketTrend,
  onTabChange
}) => {
  const theme = useTheme();
  const report = marketReport?.market_report;
  const trendReports = marketReport?.trend_reports;
  
  if (!report) {
    return (
      <Alert severity="warning">
        No market report data available. Please refresh or try again later.
      </Alert>
    );
  }

  // Helper function to extract ticker symbols from text
  const extractTickers = (text: string): string[] => {
    if (!text) return [];
    const tickerRegex = /\$([A-Z0-9]+)/g;
    const matches = text.match(tickerRegex);
    return matches ? matches.map(match => match.substring(1)) : [];
  };

  // Extract investment recommendations
  const investRecommendations = report.InvestmentRecommendations?.filter(
    (r: any) => r.RecommendationType === 'invest'
  ) || [];
  
  const reduceRecommendations = report.InvestmentRecommendations?.filter(
    (r: any) => r.RecommendationType === 'reduce'
  ) || [];

  const considerRecommendations = report.InvestmentRecommendations?.filter(
    (r: any) => r.RecommendationType === 'consider'
  ) || [];
  
  // Get market metrics
  const marketRating = report.MarketRating || 'Unknown';
  const marketTrend = report.MarketTrend || 'Unknown';
  const marketRegime = report.MarketRegime || 'Unknown';
  
  // // Helper function to get color based on market status
  // const getMarketStatusColor = () => {
  //   if (marketRating.includes('BULLISH')) {
  //     return getSignalColor(marketRating);
  //   } else if (marketRating.includes('BEARISH')) {
  //     return getSignalColor(marketRating);
  //   }
  //   return getSignalColor(marketRating);
  // };

  // Helper function to get icon based on market status
  // const getMarketStatusIcon = () => {
  //   if (marketRating.includes('BULLISH')) {
  //     return <TrendingUp sx={{ color: getSignalColor(marketRating), mr: 1 }} />;
  //   } else if (marketRating.includes('BEARISH')) {
  //     return <TrendingDown sx={{ color: getSignalColor(marketRating), mr: 1 }} />;
  //   }
  //   return <CompareArrows sx={{ color: getSignalColor(marketRating), mr: 1 }} />;
  // };

  const getTrendIconAndColor = (trend: string) => {
    if (!trend) {
      return { icon: <Info fontSize="small" />, color: theme.palette.grey[500] };
    }

    const trendLower = trend.toLowerCase();
    //console.log('******************trendLower---------------------', trendLower);
    if (trendLower.includes('Bullish')) {
      return { icon: <ArrowUpward sx={{ color: getSignalColor(trend), mr: 1 }} />, color: getSignalColor(trend) };
    } else if (trendLower.includes('Moderately Bullish')) {
      return { icon: <TrendingUp sx={{ color: getSignalColor(trend), mr: 1 }} />, color: getSignalColor(trend) };
    } else if (trendLower.includes('Neutral')) {
      return { icon: <CompareArrows sx={{ color: getSignalColor(trend), mr: 1 }} />, color: getSignalColor(trend) };
    } else if (trendLower.includes('MODERATELY BEARISH') || trendLower.includes('deteriorating')) {
      return { icon: <TrendingDown sx={{ color: getSignalColor(trend), mr: 1 }} />, color: getSignalColor(trend) };
    } else if (trendLower.includes('Bearish')) {
      return { icon: <ArrowDownward sx={{ color: getSignalColor(trend), mr: 1 }} />, color: getSignalColor(trend) };
    } else {
    return { icon: <MonetizationOn sx={{ color: getSignalColor(trend), mr: 1 }} />, color: getSignalColor(trend) };
    }
  };

  // Helper function to get status text based on market rating
  const getMarketStatusText = () => {
    if (marketRating.includes('BULLISH')) {
      return 'Favorable Conditions';
    } else if (marketRating === 'MODERATELY BEARISH') {
      return 'Exercise Caution';
    } else if (marketRating === 'BEARISH') {
      return 'Defensive Positioning';
    }
    return 'Mixed Signals';
  };

  // Helper function to get actions based on market rating
  const getRecommendedActions = () => {
    if (marketRating.includes('BULLISH')) {
      return [
        'Consider increasing equity exposure',
        'Focus on growth sectors with strong momentum',
        'Maintain disciplined investment approach',
        'Look for strategic entry points in trending sectors'
      ];
    } else if (marketRating.includes('BEARISH')) {
      return [
        'Review portfolio for high-risk positions',
        'Consider defensive sectors like utilities and consumer staples',
        'Maintain higher cash positions than normal',
        'Focus on quality over growth potential'
      ];
    }
    return [
      'Rebalance portfolio to align with market conditions',
      'Selectively add to high-conviction positions',
      'Maintain balanced exposure across sectors',
      'Continue regular investment schedule'
    ];
  };

  // Helper function to get important considerations
  const getImportantConsiderations = () => {
    return [
      `Market trend is currently ${marketTrend}`,
      `Money flow is ${trendReports?.['Money Flow']?.MarketAllUp?.Report?.Rating || 'mixed'}`,
      `Risk levels are ${trendReports?.Risk?.MarketAllUp?.Report?.Rating || 'moderate'}`,
    ];
  };

  return (
    <Box>
      
      {/* Question 1: How is the market today? */}
      <QuestionCard elevation={3}>
        <QuestionHeader>
          <QuestionAnswer sx={{ color: theme.palette.primary.main, mr: 2 }} />
          <Typography variant="h6" fontWeight="bold">
            How is the market today?
          </Typography>
        </QuestionHeader>
        
        <AnswerContent>
          <Box sx={{ 
            p: 2, 
            mb: 3, 
            borderRadius: 2, 
            //bgcolor: `${getMarketStatusColor()}15`,
            //border: `1px solid ${getMarketStatusColor()}`
          }}>
            
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' /*color: getMarketStatusColor() */}}>
              <Box component="span" sx={{ ml: 1 }}>
                <span style={{ color: getTrendIconAndColor(marketRating).color }}>
                {/* {getTrendIconAndColor(marketRating).icon} {marketRating} */}
                {marketRating}
                </span>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  {marketRating.includes('BEARISH') ? 
                    'Current market conditions suggest defensive positioning. Focus on risk management and preserving capital.' : 
                  marketRating.includes('NEUTRAL') ? 
                    'Market shows mixed signals. Balance between growth opportunities and risk management is advised.' : 
                    'Market conditions are favorable. Consider increasing exposure to growth opportunities while maintaining diversification.'}
                </Typography>
              </Box>
            </Typography>
            
          </Box>
          
          {/* Market Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Current Assessment Card */}
            <Grid item xs={12}>
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ 
                    fontWeight: 'bold', 
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    mb: 1
                  }}>
                    <Assessment sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
                    Current Assessment
                  </Typography>
                  <Typography variant="body1">
                    The market is currently "{marketRating.toLowerCase()}" and {marketTrend.toLowerCase()}.<br/>
                    {report?.MarketAllUp?.Report?.RatingTrendExplanation || ''}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Performance Metrics Card */}
            <Grid item xs={12} md={4}>
              <Card elevation={1} sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ 
                    fontWeight: 'bold', 
                    color: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2
                  }}>
                    <ShowChart sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
                    Performance
                  </Typography>
                  <Box sx={{ 
                    '& > *': { mb: 1.5 },
                    '& > *:last-child': { mb: 0 }
                  }}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                      • Rating: {trendReports?.Performance?.MarketAllUp?.Report?.Rating || 'N/A'}
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                      • Short-term: {report.ProjectedMarketTrend_ShortTerm || 'Unknown'}
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                      • Long-term: {report.ProjectedMarketTrend_LongTerm || 'Unknown'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Market Indicators Card */}
            <Grid item xs={12} md={4}>
              <Card elevation={1} sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ 
                    fontWeight: 'bold', 
                    color: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2
                  }}>
                    <Info sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
                    Key Indicators
                  </Typography>
                  <Box sx={{ 
                    '& > *': { mb: 1.5 },
                    '& > *:last-child': { mb: 0 }
                  }}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                      • Market Regime: {marketRegime}
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                      • Money Flow: {trendReports?.['Money Flow']?.MarketAllUp?.Report?.Rating || 'N/A'}
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                      • Risk Level: {trendReports?.Risk?.MarketAllUp?.Report?.Rating || 'N/A'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Market Trends Card */}
            <Grid item xs={12} md={4}>
              <Card elevation={1} sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ 
                    fontWeight: 'bold', 
                    color: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2
                  }}>
                    <ArrowForward sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
                    Current Trends
                  </Typography>
                  <Box sx={{ 
                    '& > *': { mb: 1.5 },
                    '& > *:last-child': { mb: 0 }
                  }}>
                    <Typography variant="body2">
                      • Market trend is {marketTrend.toLowerCase()}
                    </Typography>
                    <Typography variant="body2">
                      • {report.MarketOverview ? report.MarketOverview?.split('.')[1] + '.' : 'No trend information available.'}
                    </Typography>
                    <Typography variant="body2">
                      • Long-term outlook: {report.ProjectedMarketTrend_LongTerm}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {/* <Button 
            variant="contained" 
            color="primary"
            onClick={() => onTabChange(0)} // Go to Dashboard
            sx={{ mt: 1 }}
          >
            View Market Dashboard
          </Button> */}
        </AnswerContent>
      </QuestionCard>
      
      {/* Question 2: What should I do today and why? */}
      <QuestionCard elevation={3}>
        <QuestionHeader>
          <QuestionAnswer sx={{ color: theme.palette.primary.main, mr: 2 }} />
          <Typography variant="h6" fontWeight="bold">
            What should I do today and why?
          </Typography>
        </QuestionHeader>
        
        <AnswerContent>
          <Box sx={{ 
            p: 2, 
            mb: 3, 
            borderRadius: 2, 
            //bgcolor: `${getMarketStatusColor()}15`,
            //border: `1px solid ${getMarketStatusColor()}`
          }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', color: theme.palette.primary.main }}>
              {/* {getTrendIconAndColor(marketTrend).icon} */}
              <Box component="span" sx={{ ml: 1 }}>
                {getMarketStatusText()}
              </Box>
            </Typography>
            
            {/* <Typography variant="body1" sx={{ mt: 1 }}>
              {marketRating.includes('BEARISH') ? 
                'Current market conditions suggest defensive positioning. Focus on risk management and preserving capital.' : 
               marketRating.includes('NEUTRAL') ? 
                'Market shows mixed signals. Balance between growth opportunities and risk management is advised.' : 
                'Market conditions are favorable. Consider increasing exposure to growth opportunities while maintaining diversification.'}
            </Typography> */}
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: getSignalColor("Bullish")}}>
                  <CheckCircle sx={{ mr: 1 }} />
                  Recommended Actions
                </Typography>
                
                
                <ul>
                  {(report?.InvestmentRecommendations || getRecommendedActions()).map((action: InvestmentRecommendation | string, index) => (
                    <li key={index}>{typeof action === 'string' ? action : action.Details.Title}</li>
                  ))}
                </ul>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: getSignalColor("Neutral")}}>
                  <Help sx={{ mr: 1 }} />
                  Important Considerations
                </Typography>
                
                <ul>
                  {getImportantConsiderations().map((consideration, index) => (
                    <li key={index}>{consideration}</li>
                  ))}
                </ul>
              </Box>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" color={theme.palette.primary.main} gutterBottom>
              Investment Philosophy:
            </Typography>
            <Paper sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
              <Typography variant="body2">
                {report.InvestmentPhilosophy || 'No investment philosophy available.'}
              </Typography>
            </Paper>
          </Box>
          
          {/* <Button 
            variant="contained" 
            color="primary"
            onClick={() => onTabChange(2)} // Go to Market Trends
            sx={{ mt: 3 }}
          >
            View Market Trends
          </Button> */}
        </AnswerContent>
      </QuestionCard>
      
      {/* Question 3: Where should I invest and why? */}
      <QuestionCard elevation={3}>
        <QuestionHeader>
          <QuestionAnswer sx={{ color: theme.palette.primary.main, mr: 2 }} />
          <Typography variant="h6" fontWeight="bold">
            Where should I invest and why?
          </Typography>
        </QuestionHeader>
        
        <AnswerContent>
          {investRecommendations.length > 0 || considerRecommendations.length > 0 ? (
            <>
              <Typography variant="body1" paragraph>
                Based on current market analysis, the following investments show potential:
              </Typography>
              
              <Grid container spacing={2}>
                {investRecommendations.concat(considerRecommendations).slice(0, 3).map((rec: any, idx: number) => {
                  const tickers = extractTickers(rec.Details.Title);
                  
                  return (
                    <Grid item xs={12} md={4} key={idx}>
                      <Card sx={{ 
                        height: '100%', 
                        borderLeft: `4px solid ${rec.RecommendationType === 'invest' ? getSignalColor("Bullish") : getSignalColor("Neutral")}` 
                      }}>
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            {rec.Details.Title}
                          </Typography>
                          
                          <Divider sx={{ my: 1 }} />
                          
                          <Typography variant="body2" paragraph>
                            {rec.Details.Reason?.split('.')[0] + '.'}
                          </Typography>
                          
                          <Box sx={{ mt: 'auto', pt: 1 }}>
                            {tickers.map(ticker => (
                              <Chip 
                                key={ticker}
                                label={ticker}
                                size="small"
                                onClick={() => onTickerSelect && onTickerSelect(ticker)}
                                sx={{ 
                                  mr: 0.5, fontWeight: 'bold', 
                                  bgcolor: rec.RecommendationType === 'invest' ? getSignalColor("Bullish") : getSignalColor("Neutral"),
                                  color: theme.palette.primary.contrastText
                                }}
                              />
                            ))}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            
            </>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              No specific investment recommendations available at this time.
            </Typography>
          )}
          
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" color={theme.palette.primary.main} gutterBottom>
              Strong Sectors:
            </Typography>
            <Grid container spacing={2}>
              {report.Sectors?.Reports
                ?.filter((s: any) => s.Rating.includes('BULLISH'))
                .slice(0, 3)
                .map((sector: any, idx: number) => (
                <Grid item xs={12} md={4} key={idx}>
                      <Card sx={{ 
                        elevation: 1,
                        height: '100%', 
                        borderLeft: `4px solid ${getSignalColor("Bullish")}` 
                      }}>
                    <CardContent>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        {sector.Classification.replace('Sector: ', '')}
                      </Typography>
                      <Typography variant="body2">
                        {sector.RatingTrendExplanation?.split('.')[0] + '.'}
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Chip 
                          size="small" 
                          label={sector.Rating}
                          sx={{
                            bgcolor: getSignalColor("Bullish"),
                            color: theme.palette.primary.contrastText
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Ideal Allocation: {sector.Allocations?.ideal || 0}%
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            
            {report.Sectors?.Reports?.filter((s: any) => s.Rating.includes('BULLISH')).length === 0 && (
              <Typography variant="body1" color="text.secondary">
                No strong sectors identified in the current market.
              </Typography>
            )}
          </Box>
        </AnswerContent>
      </QuestionCard>
      
      {/* Question 4: What should I be cautious of and why? */}
      <QuestionCard elevation={3}>
        <QuestionHeader>
          <QuestionAnswer sx={{ color: theme.palette.primary.main, mr: 2 }} />
          <Typography variant="h6" fontWeight="bold">
            What should I be cautious of and why?
          </Typography>
        </QuestionHeader>
        
        <AnswerContent>
          {reduceRecommendations.length > 0 ? (
            <>
              <Typography variant="body1" paragraph>
                Current analysis suggests caution with these investments:
              </Typography>
              
              <Grid container spacing={2}>
                {reduceRecommendations.slice(0, 3).map((rec: any, idx: number) => {
                  const tickers = extractTickers(rec.Details.Title);
                  
                  return (
                    <Grid item xs={12} md={4} key={idx}>
                      <Card sx={{ 
                        height: '100%', 
                        borderLeft: `4px solid ${getSignalColor("Bearish")}` 
                      }}>
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            {rec.Details.Title}
                          </Typography>
                          
                          <Divider sx={{ my: 1 }} />
                          
                          <Typography variant="body2" paragraph>
                            {rec.Details.Reason?.split('.')[0] + '.'}
                          </Typography>
                          
                          <Box sx={{ mt: 'auto', pt: 1 }}>
                            {tickers.map(ticker => (
                              <Chip 
                                key={ticker}
                                label={ticker}
                                size="small"
                                onClick={() => onTickerSelect && onTickerSelect(ticker)}
                                sx={{ 
                                  mr: 0.5, 
                                  fontWeight: 'bold', 
                                  bgcolor: getSignalColor("Bearish"),
                                  color: theme.palette.primary.contrastText
                                }}
                              />
                            ))}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              No specific caution areas available at this time.
            </Typography>
          )}
          
          <Box sx={{ mt: 3 }}>
            {/* <Typography variant="subtitle1" fontWeight="bold" color={theme.palette.primary.main} gutterBottom>
              Key Risk Factors:
            </Typography> */}
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" color={getSignalColor("Bearish")}>
                      Market Risks
                    </Typography>
                    <Typography variant="body2">
                      {trendReports?.Risk?.Recommendations + '.' || 
                        'Current market volatility suggests caution with high-beta investments. Monitor economic indicators closely.'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
            </Grid>
          </Box>
          
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" color={theme.palette.primary.main} gutterBottom>
              Weak Sectors:
            </Typography>
            <Grid container spacing={2}>
              {report.Sectors?.Reports
                ?.filter((s: any) => s.Rating.includes('BEARISH'))
                .slice(0, 3)
                .map((sector: any, idx: number) => (
                <Grid item xs={12} md={4} key={idx}>
                      <Card sx={{ 
                        elevation: 1,
                        height: '100%', 
                        borderLeft: `4px solid ${getSignalColor("Bearish")}` 
                      }}>
                    <CardContent>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        {sector.Classification.replace('Sector: ', '')}
                      </Typography>
                      <Typography variant="body2">
                        {sector.RatingTrendExplanation?.split('.')[0] + '.'}
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Chip 
                          size="small" 
                          label={sector.Rating}
                          sx={{
                            bgcolor: getSignalColor("Bearish"),
                            color: theme.palette.primary.contrastText
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Ideal Allocation: {sector.Allocations?.ideal || 0}%
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            
            {report.Sectors?.Reports?.filter((s: any) => s.Rating.includes('BEARISH')).length === 0 && (
              <Typography variant="body1" color="text.secondary">
                No particularly weak sectors identified in the current market.
              </Typography>
            )}
          </Box>
          
          {/* <Button 
            variant="contained" 
            color="primary"
            onClick={() => onTabChange(2, 1)} // Go to Market Trends with Risk tab (index 1)
            sx={{ mt: 3 }}
          >
            View Full Risk Analysis
          </Button> */}
        </AnswerContent>
      </QuestionCard>
    </Box>
  );
};

export default MarketQuestionsView; 